import { useState, type FormEvent } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { useSeo } from '@/hooks/useSeo';
import { site } from '@/utils/site';

interface FormResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/daihqqjp14a1h3mr74g0';

const contactCards = [
  {
    icon: 'ri-mail-line',
    title: '内容与推荐',
    value: site.email,
    desc: '推荐名场面、内容纠错与使用反馈，都可以发到这里。',
  },
  {
    icon: 'ri-team-line',
    title: '商务与合作',
    value: site.bizEmail,
    desc: '栏目共建、内容授权与品牌合作事宜，请注明来意。',
  },
  {
    icon: 'ri-time-line',
    title: '响应时间',
    value: '工作日 2 个工作日内',
    desc: '收到留言后我们会按提交顺序逐条处理。',
  },
];

export default function ContactPage() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [formError, setFormError] = useState('');

  useSeo({
    title: '联系我们_看点推荐与内容反馈',
    description:
      '联系黑料看点：推荐你心中的高能名场面、反馈内容问题或洽谈合作。填写表单即可，编辑团队会在两个工作日内回复。',
    keywords: '黑料看点联系,黑料看点投稿,黑料看点什么,推荐名场面',
    path: '/contact',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: '联系黑料看点',
        description: '推荐名场面、内容反馈与商务合作联系方式。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: '联系我们' },
        ],
      },
    ],
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') {
        return;
      }
      if (typeof value === 'string') {
        if (value.trim() === '') {
          return;
        }
        payload.append(key, value);
      }
    });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        '';
      const code = parsed?.code ?? '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-24 md:pt-32 pb-10 md:pb-14 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '联系我们' }]} />
          <h1 className="mt-5 font-heading text-3xl md:text-5xl text-foreground-950">联系我们</h1>
          <p className="mt-4 max-w-2xl text-sm md:text-base text-foreground-600 leading-8">
            有想推荐的名场面、发现内容有问题，或者想聊聊合作？填一下下面的表单，或者直接给我们发邮件都行。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-5 space-y-4">
            {contactCards.map((card) => (
              <div key={card.title} className="bg-background-100 rounded-lg border border-background-200 p-5 md:p-6">
                <div className="flex items-start gap-4">
                  <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 text-xl">
                    <i className={card.icon} />
                  </span>
                  <div className="min-w-0">
                    <h2 className="font-heading text-lg text-foreground-950">{card.title}</h2>
                    <p className="mt-1 text-sm font-medium text-foreground-800 break-all">{card.value}</p>
                    <p className="mt-2 text-xs text-foreground-500 leading-6">{card.desc}</p>
                  </div>
                </div>
              </div>
            ))}

            <div className="rounded-lg bg-accent-100 border border-accent-200 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                <i className="ri-lightbulb-line text-accent-700" />
                推荐小贴士
              </h2>
              <ul className="mt-3 space-y-2 text-sm text-foreground-700 leading-7">
                <li className="flex gap-2">
                  <i className="ri-checkbox-blank-circle-fill text-[8px] mt-3 text-accent-600" />
                  写清节目名称与大致时间点，方便我们快速定位。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-blank-circle-fill text-[8px] mt-3 text-accent-600" />
                  一句话说明推荐理由，我们会更清楚它为什么值得做成合集。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-blank-circle-fill text-[8px] mt-3 text-accent-600" />
                  内容纠错请附上页面标题和具体问题，便于核实。
                </li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">留言给我们</h2>
              <p className="mt-2 text-sm text-foreground-500">带 * 的为必填项，留言内容请控制在 500 字以内。</p>

              {status === 'success' && (
                <div className="mt-6 flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
                  <i className="ri-checkbox-circle-fill text-primary-600 text-lg mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-primary-800">已收到你的留言，谢谢！</p>
                    <p className="mt-1 text-xs text-primary-700">
                      编辑团队会在两个工作日内处理，如涉及内容纠错会优先跟进。
                    </p>
                  </div>
                </div>
              )}

              {status === 'error' && (
                <div className="mt-6 flex items-start gap-3 rounded-lg border border-primary-300 bg-primary-50 px-4 py-3">
                  <i className="ri-error-warning-fill text-primary-600 text-lg mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-primary-800">提交未成功</p>
                    <p className="mt-1 text-xs text-primary-700">{formError}</p>
                  </div>
                </div>
              )}

              <form data-readdy-form onSubmit={handleSubmit} className="mt-6 space-y-5" noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm text-foreground-800 mb-2">
                      称呼 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      autoComplete="name"
                      placeholder="怎么称呼你"
                      className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm text-foreground-800 mb-2">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="方便回复的邮箱"
                      className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-topic" className="block text-sm text-foreground-800 mb-2">
                    留言类型 <span className="text-primary-500">*</span>
                  </label>
                  <select
                    id="contact-topic"
                    name="topic"
                    required
                    defaultValue="推荐名场面"
                    className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
                  >
                    <option value="推荐名场面">推荐名场面</option>
                    <option value="内容纠错">内容纠错</option>
                    <option value="商务合作">商务合作</option>
                    <option value="其他建议">其他建议</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm text-foreground-800 mb-2">
                    具体内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    placeholder="例如：《某个节目》第 8 期 32 分钟左右那一段反转特别精彩，推荐做成合集。"
                    className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 resize-none"
                  />
                  <p className="mt-1.5 text-xs text-foreground-400">最多 500 字，内容简洁一点我们会处理得更快。</p>
                </div>

                <div className="contact-form-alt-field" aria-hidden="true">
                  <label htmlFor="contact-alt">请勿填写此项</label>
                  <input
                    id="contact-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i className={status === 'submitting' ? 'ri-loader-4-line' : 'ri-send-plane-line'} />
                    {status === 'submitting' ? '正在提交…' : '提交留言'}
                  </button>
                  <p className="text-xs text-foreground-500">
                    我们只会用你的联系方式回复本条留言，不会用于其他用途。
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}